/**
 * Authenticity Home - Add to Project & Presentation Engine
 * Handles customer interaction, quick project/presentation creation, and catalog curation.
 * Features portal-parity logics: variant/color/size selection, custom rug dimensions, and dynamic pricing.
 */

(function () {
  'use strict';

  // Base API configuration
  const API_BASE = (window.__AUTHENTICITY_API_URL__ || 'https://hub.authenticity.shop').replace(/\/$/, '');

  // State
  let currentProduct = null;
  let activeTab = 'project'; // 'project' | 'presentation'
  let cachedProjects = null;
  let cachedPresentations = null;
  let isSubmitting = false;

  // DOM Elements
  let overlay, modal, closeBtn, cancelBtn, submitBtn, submitText, submitSpinner;
  let previewImage, previewTitle, previewMeta, productTypeBadge;
  let productConfigSection, variantGroup, selectVariant, customDimsGroup;
  let dimWidthFt, dimWidthIn, dimLengthFt, dimLengthIn;
  let selectProject, selectPresentation, newPresentationProjectSelect;
  let projectExistingFields, projectNewFields, presentationExistingFields, presentationNewFields;
  let newProjectNameInput, newProjectNotesInput, projectItemRoomInput, projectItemNotesInput;
  let newPresentationTitleInput;
  let toastContainer;

  // -------------------------------------------------------------------------
  // Portal Parity Helper Functions (Type, Sizing, Dimensions)
  // -------------------------------------------------------------------------

  function isCustomProductType(productType, tags) {
    const value = String(productType || '').trim();
    if (/\bcustom\b/i.test(value)) return true;
    if (/\bin\s*stock\b/i.test(value)) return false;

    const tagList = Array.isArray(tags) ? tags : String(tags || '').split(',');
    const normalizedTags = tagList.map((t) => String(t || '').trim().toLowerCase()).filter(Boolean);
    if (normalizedTags.some((t) => t === 'custom' || t === 'custom rug' || t === 'made to order' || t === 'mto')) return true;
    if (normalizedTags.some((t) => t === 'in stock' || t === 'instock')) return false;

    return false;
  }

  function parseRugMeasurePart(value) {
    const text = String(value || '')
      .replace(/[′’]/g, "'")
      .replace(/[″“”]/g, '"')
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return null;

    const match = text.match(
      /^(\d+(?:\.\d+)?)\s*(?:ft|feet|')?(?:\s*-?\s*(\d+(?:\.\d+)?)\s*(?:in|inches|")?)?$/i
    );
    if (!match || !match[1]) return null;

    const feetRaw = Number(match[1]);
    const inchesRaw = match[2] != null && match[2] !== '' ? Number(match[2]) : 0;
    if (!Number.isFinite(feetRaw) || feetRaw <= 0) return null;

    const feetWhole = Math.floor(feetRaw);
    const totalInches = feetWhole * 12 + inchesRaw;
    const valueFeet = totalInches / 12;

    return {
      value: valueFeet,
      feet: feetWhole,
      inches: inchesRaw,
    };
  }

  function parseRugDimensionsFromText(value) {
    const text = String(value || '').trim();
    if (!text) return null;
    const parts = text.split(/\s*[x×]\s*/i);
    if (parts.length !== 2) return null;

    const widthPart = parseRugMeasurePart(parts[0]);
    const lengthPart = parseRugMeasurePart(parts[1]);
    if (!widthPart || !lengthPart) return null;

    return {
      width: widthPart.value,
      length: lengthPart.value,
      widthFeet: widthPart.feet,
      widthInches: widthPart.inches,
      lengthFeet: lengthPart.feet,
      lengthInches: lengthPart.inches,
    };
  }

  function combineRugDimensionInputs(feetValue, inchesValue) {
    const hasFeet = feetValue !== '' && feetValue != null;
    const hasInches = inchesValue !== '' && inchesValue != null;
    if (!hasFeet && !hasInches) return null;

    const feetNumber = hasFeet ? Number(feetValue) : 0;
    const inchesNumber = hasInches ? Number(inchesValue) : 0;
    if (!Number.isFinite(feetNumber) || feetNumber < 0) return null;
    if (!Number.isFinite(inchesNumber) || inchesNumber < 0) return null;

    const totalInches = Math.floor(feetNumber) * 12 + inchesNumber;
    return totalInches > 0 ? totalInches / 12 : null;
  }

  // -------------------------------------------------------------------------
  // Initialization & Event Binding
  // -------------------------------------------------------------------------

  function init() {
    overlay = document.getElementById('ah-project-modal-overlay');
    if (!overlay) return;

    modal = overlay.querySelector('.ah-modal');
    closeBtn = document.getElementById('ah-modal-close');
    cancelBtn = document.getElementById('ah-modal-cancel');
    submitBtn = document.getElementById('ah-modal-submit');
    submitText = submitBtn ? submitBtn.querySelector('.ah-btn__text') : null;
    submitSpinner = submitBtn ? submitBtn.querySelector('.ah-spinner') : null;

    previewImage = document.getElementById('ah-preview-image');
    previewTitle = document.getElementById('ah-preview-title');
    previewMeta = document.getElementById('ah-preview-meta');
    productTypeBadge = document.getElementById('ah-product-type-badge');

    productConfigSection = document.getElementById('ah-product-config');
    variantGroup = document.getElementById('ah-variant-group');
    selectVariant = document.getElementById('ah-select-variant');
    customDimsGroup = document.getElementById('ah-custom-dims-group');

    dimWidthFt = document.getElementById('ah-dim-width-ft');
    dimWidthIn = document.getElementById('ah-dim-width-in');
    dimLengthFt = document.getElementById('ah-dim-length-ft');
    dimLengthIn = document.getElementById('ah-dim-length-in');

    selectProject = document.getElementById('ah-select-project');
    selectPresentation = document.getElementById('ah-select-presentation');
    newPresentationProjectSelect = document.getElementById('ah-new-presentation-project');

    projectExistingFields = document.getElementById('ah-project-existing-fields');
    projectNewFields = document.getElementById('ah-project-new-fields');
    presentationExistingFields = document.getElementById('ah-presentation-existing-fields');
    presentationNewFields = document.getElementById('ah-presentation-new-fields');

    newProjectNameInput = document.getElementById('ah-new-project-name');
    newProjectNotesInput = document.getElementById('ah-new-project-notes');
    projectItemRoomInput = document.getElementById('ah-project-item-room');
    projectItemNotesInput = document.getElementById('ah-project-item-notes');
    newPresentationTitleInput = document.getElementById('ah-new-presentation-title');

    toastContainer = document.getElementById('ah-toast-container');

    attachGlobalEvents();
    checkHubSession();
  }

  async function checkHubSession() {
    const liquidCustomer = window.__AUTHENTICITY_CUSTOMER__;

    // 1. If Liquid confirmed login with valid email
    if (liquidCustomer && liquidCustomer.isLoggedIn && liquidCustomer.email) {
      revealAddButtons();
      updateHeaderAccountLinks(true);
      return;
    }

    // 2. Otherwise, check active session on Authenticity Hub
    try {
      const res = await apiRequest('/api/client/account');
      if (res && res.ok && res.account && res.account.email) {
        window.__AUTHENTICITY_CUSTOMER__ = {
          isLoggedIn: true,
          id: res.account.id,
          email: res.account.email,
          name: res.account.displayName || res.account.firstName || res.account.name || 'Customer',
        };
        revealAddButtons();
        updateHeaderAccountLinks(true);
      } else {
        hideAddButtons();
        updateHeaderAccountLinks(false);
      }
    } catch (e) {
      // Visitor not logged in on Hub or Storefront
      hideAddButtons();
      updateHeaderAccountLinks(false);
    }
  }

  function revealAddButtons() {
    const wrappers = document.querySelectorAll('.card__add-project-wrapper');
    wrappers.forEach(function (w) {
      w.style.display = 'inline-flex';
    });
  }

  function hideAddButtons() {
    const wrappers = document.querySelectorAll('.card__add-project-wrapper');
    wrappers.forEach(function (w) {
      w.style.display = 'none';
    });
  }

  function updateHeaderAccountLinks(isLoggedIn) {
    const hubUrl = getFullPortalUrl('/client');
    const accountLinks = document.querySelectorAll(
      '.wt-header__panel__item--account a, .wt-page-nav-mega__aside-list__item a, .wt-cart__login__text a'
    );
    accountLinks.forEach(function (link) {
      if (isLoggedIn) {
        if (link.href && (link.href.includes('/account/login') || link.href.includes('/client/login'))) {
          link.href = hubUrl;
        }
      }
    });
  }

  function attachGlobalEvents() {
    // Event delegation for "Add" button on product cards
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.btn-add-to-project');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        handleCardAddClick(btn);
      }
    });

    // Close buttons
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (cancelBtn) cancelBtn.addEventListener('click', closeModal);

    // Click outside modal
    if (overlay) {
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) closeModal();
      });
    }

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay && overlay.classList.contains('is-active')) {
        closeModal();
      }
    });

    // Re-verify session when tab regains focus or visibility
    window.addEventListener('focus', function () {
      if (document.visibilityState === 'visible') {
        checkHubSession();
      }
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        checkHubSession();
      }
    });

    // Periodic heartbeat to keep session in sync
    setInterval(function () {
      checkHubSession();
    }, 10 * 60 * 1000);

    // Tab switching
    const tabButtons = document.querySelectorAll('.ah-tab-btn');
    tabButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const tab = this.getAttribute('data-tab');
        switchTab(tab);
      });
    });

    // Project mode radio toggle (Existing vs New)
    const projectModeRadios = document.querySelectorAll('input[name="ah-project-mode"]');
    projectModeRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (this.value === 'existing') {
          if (projectExistingFields) projectExistingFields.style.display = 'block';
          if (projectNewFields) projectNewFields.style.display = 'none';
        } else {
          if (projectExistingFields) projectExistingFields.style.display = 'none';
          if (projectNewFields) projectNewFields.style.display = 'block';
        }
        updateSubmitButtonText();
      });
    });

    // Presentation mode radio toggle (Existing vs New)
    const presentationModeRadios = document.querySelectorAll('input[name="ah-presentation-mode"]');
    presentationModeRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (this.value === 'existing') {
          if (presentationExistingFields) presentationExistingFields.style.display = 'block';
          if (presentationNewFields) presentationNewFields.style.display = 'none';
        } else {
          if (presentationExistingFields) presentationExistingFields.style.display = 'none';
          if (presentationNewFields) presentationNewFields.style.display = 'block';
        }
        updateSubmitButtonText();
      });
    });

    // Variant selector change handler
    if (selectVariant) {
      selectVariant.addEventListener('change', handleVariantSelectionChange);
    }

    // Submit handler
    if (submitBtn) {
      submitBtn.addEventListener('click', handleSubmit);
    }
  }

  // -------------------------------------------------------------------------
  // Product Selection & Modal Setup
  // -------------------------------------------------------------------------

  function handleCardAddClick(btn) {
    // Check if customer is logged in
    const customer = window.__AUTHENTICITY_CUSTOMER__;
    if (!customer || !customer.isLoggedIn) {
      showToast('Please sign in to your trade account to save items to projects.', 'error');
      const returnTo = encodeURIComponent(window.location.href);
      window.location.href = 'https://hub.authenticity.shop/client/login?returnTo=' + returnTo;
      return;
    }

    // Parse variants JSON safely
    let variants = [];
    try {
      const rawVariants = btn.getAttribute('data-variants');
      if (rawVariants) {
        variants = JSON.parse(rawVariants);
      }
    } catch (e) {
      variants = [];
    }

    // Read product data attributes
    const rawTags = btn.getAttribute('data-tags') || '';
    const productType = btn.getAttribute('data-product-type') || '';
    const isCustom = isCustomProductType(productType, rawTags);

    currentProduct = {
      productId: btn.getAttribute('data-product-id') || '',
      variantId: btn.getAttribute('data-variant-id') || '',
      title: btn.getAttribute('data-title') || 'Product',
      sku: btn.getAttribute('data-sku') || '',
      image: btn.getAttribute('data-image') || '',
      price: parseFloat(btn.getAttribute('data-price')) || null,
      vendor: btn.getAttribute('data-vendor') || '',
      productType: productType,
      size: btn.getAttribute('data-size') || '',
      tags: rawTags ? rawTags.split(',') : [],
      material: btn.getAttribute('data-material') || '',
      origin: btn.getAttribute('data-origin') || '',
      texture: btn.getAttribute('data-texture') || '',
      pileHeight: btn.getAttribute('data-pile-height') || '',
      construction: btn.getAttribute('data-construction') || '',
      description: btn.getAttribute('data-description') || '',
      variants: Array.isArray(variants) ? variants : [],
      isCustom: isCustom,
    };

    // Render Product Configuration (Variants, Colors, Sizes & Custom Dimensions)
    setupProductConfigUI();

    // Reset Form inputs
    if (newProjectNameInput) newProjectNameInput.value = '';
    if (newProjectNotesInput) newProjectNotesInput.value = '';
    if (projectItemRoomInput) projectItemRoomInput.value = '';
    if (projectItemNotesInput) projectItemNotesInput.value = '';
    if (newPresentationTitleInput) newPresentationTitleInput.value = '';

    // Switch to default project tab
    switchTab('project');

    // Open modal
    openModal();

    // Fetch live projects and presentations
    loadProjectsAndPresentations();
  }

  function setupProductConfigUI() {
    if (!currentProduct) return;

    // 1. Badge & Type indicator
    if (productTypeBadge) {
      if (currentProduct.isCustom) {
        productTypeBadge.textContent = 'Custom Rug';
        productTypeBadge.className = 'ah-badge ah-badge--custom';
        productTypeBadge.style.display = 'inline-flex';
      } else if (currentProduct.productType) {
        productTypeBadge.textContent = currentProduct.productType;
        productTypeBadge.className = 'ah-badge ah-badge--type';
        productTypeBadge.style.display = 'inline-flex';
      } else {
        productTypeBadge.style.display = 'none';
      }
    }

    // 2. Variants Selector
    const hasMultipleVariants = currentProduct.variants.length > 1;
    if (selectVariant && variantGroup) {
      selectVariant.innerHTML = '';
      if (hasMultipleVariants) {
        currentProduct.variants.forEach((v) => {
          const opt = document.createElement('option');
          opt.value = v.id;
          const formattedPrice = v.price ? ' — $' + Number(v.price).toLocaleString() : '';
          const optTitle = v.title && v.title.toLowerCase() !== 'default title' ? v.title : 'Standard';
          opt.textContent = optTitle + formattedPrice;
          if (String(v.id) === String(currentProduct.variantId)) {
            opt.selected = true;
          }
          selectVariant.appendChild(opt);
        });
        variantGroup.style.display = 'block';
      } else {
        variantGroup.style.display = 'none';
      }
    }

    // 3. Custom Sizing vs Auto Sizing
    if (customDimsGroup) {
      if (currentProduct.isCustom) {
        customDimsGroup.style.display = 'block';
        if (dimWidthFt) dimWidthFt.value = '';
        if (dimWidthIn) dimWidthIn.value = '';
        if (dimLengthFt) dimLengthFt.value = '';
        if (dimLengthIn) dimLengthIn.value = '';
      } else {
        customDimsGroup.style.display = 'none';
      }
    }

    // 4. Show/Hide Config Section Container
    if (productConfigSection) {
      if (hasMultipleVariants || currentProduct.isCustom) {
        productConfigSection.style.display = 'block';
      } else {
        productConfigSection.style.display = 'none';
      }
    }

    // Update initial preview details
    updatePreviewDisplay();
  }

  function handleVariantSelectionChange() {
    if (!currentProduct || !selectVariant) return;

    const selectedId = selectVariant.value;
    const selectedVariant = currentProduct.variants.find((v) => String(v.id) === String(selectedId));

    if (selectedVariant) {
      currentProduct.variantId = selectedVariant.id;
      if (selectedVariant.sku) currentProduct.sku = selectedVariant.sku;
      if (selectedVariant.price != null) currentProduct.price = selectedVariant.price;
      if (selectedVariant.image) currentProduct.image = selectedVariant.image;
      if (selectedVariant.title && selectedVariant.title.toLowerCase() !== 'default title') {
        currentProduct.size = selectedVariant.title;
      }
    }

    updatePreviewDisplay();
  }

  function updatePreviewDisplay() {
    if (!currentProduct) return;

    if (previewImage) previewImage.src = currentProduct.image || '';
    if (previewTitle) previewTitle.textContent = currentProduct.title;

    if (previewMeta) {
      const metaParts = [];
      if (currentProduct.size) metaParts.push(currentProduct.size);
      if (currentProduct.sku) metaParts.push('SKU: ' + currentProduct.sku);
      if (currentProduct.price != null) metaParts.push('$' + Number(currentProduct.price).toLocaleString());
      previewMeta.textContent = metaParts.join(' • ') || 'Ready to add';
    }
  }

  // -------------------------------------------------------------------------
  // Tabs, Modal & API
  // -------------------------------------------------------------------------

  function switchTab(tab) {
    activeTab = tab;
    const tabButtons = document.querySelectorAll('.ah-tab-btn');
    tabButtons.forEach(function (btn) {
      if (btn.getAttribute('data-tab') === tab) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });

    const projectPanel = document.getElementById('ah-panel-project');
    const presentationPanel = document.getElementById('ah-panel-presentation');

    if (tab === 'project') {
      if (projectPanel) projectPanel.style.display = 'block';
      if (presentationPanel) presentationPanel.style.display = 'none';
    } else {
      if (projectPanel) projectPanel.style.display = 'none';
      if (presentationPanel) presentationPanel.style.display = 'block';
    }

    updateSubmitButtonText();
  }

  function updateSubmitButtonText() {
    if (!submitText) return;

    if (activeTab === 'project') {
      const isNew = document.querySelector('input[name="ah-project-mode"]:checked')?.value === 'new';
      submitText.textContent = isNew ? 'Create & Add to Project' : 'Add to Project';
    } else {
      const isNew = document.querySelector('input[name="ah-presentation-mode"]:checked')?.value === 'new';
      submitText.textContent = isNew ? 'Create & Add Slide' : 'Add to Presentation';
    }
  }

  function openModal() {
    if (!overlay) return;
    overlay.classList.add('is-active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    if (!overlay) return;
    overlay.classList.remove('is-active');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  async function apiRequest(endpoint, options = {}) {
    let rawApiBase = (window.__AUTHENTICITY_API_URL__ || 'https://hub.authenticity.shop').trim().replace(/\/$/, '');
    if (rawApiBase.endsWith('/api')) {
      rawApiBase = rawApiBase.slice(0, -4);
    }
    const url = endpoint.startsWith('http') ? endpoint : rawApiBase + endpoint;

    const customer = window.__AUTHENTICITY_CUSTOMER__ || {};
    const defaultHeaders = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (customer.email) {
      defaultHeaders['x-customer-email'] = customer.email;
    }
    if (customer.id) {
      defaultHeaders['x-customer-id'] = String(customer.id);
    }
    if (customer.name) {
      defaultHeaders['x-customer-name'] = customer.name;
    }

    const res = await fetch(url, {
      ...options,
      headers: { ...defaultHeaders, ...(options.headers || {}) },
      credentials: 'include', // sends cookies
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.message || 'Request failed (' + res.status + ')');
      err.status = res.status;
      throw err;
    }
    return data;
  }

  async function loadProjectsAndPresentations() {
    try {
      const [projectsRes, presentationsRes] = await Promise.all([
        apiRequest('/api/client/projects').catch(() => ({ projects: [] })),
        apiRequest('/api/client/presentations').catch(() => ({ presentations: [] })),
      ]);

      cachedProjects = projectsRes.projects || [];
      cachedPresentations = presentationsRes.presentations || [];

      renderProjectOptions();
      renderPresentationOptions();
    } catch (err) {
      console.error('Failed to load portal data:', err);
    }
  }

  function renderProjectOptions() {
    if (!selectProject) return;

    selectProject.innerHTML = '';
    if (!cachedProjects || cachedProjects.length === 0) {
      selectProject.innerHTML = '<option value="">No existing projects found</option>';
      const newRadio = document.querySelector('input[name="ah-project-mode"][value="new"]');
      if (newRadio) {
        newRadio.checked = true;
        newRadio.dispatchEvent(new Event('change'));
      }
      return;
    }

    cachedProjects.forEach(function (p) {
      const opt = document.createElement('option');
      opt.value = p.id;
      opt.textContent = p.name + (p.itemCount ? ' (' + p.itemCount + ' items)' : '');
      selectProject.appendChild(opt);
    });

    if (newPresentationProjectSelect) {
      newPresentationProjectSelect.innerHTML = '<option value="">No Project (Standalone)</option>';
      cachedProjects.forEach(function (p) {
        const opt = document.createElement('option');
        opt.value = p.id;
        opt.textContent = p.name;
        newPresentationProjectSelect.appendChild(opt);
      });
    }
  }

  function renderPresentationOptions() {
    if (!selectPresentation) return;

    selectPresentation.innerHTML = '';
    if (!cachedPresentations || cachedPresentations.length === 0) {
      selectPresentation.innerHTML = '<option value="">No existing presentations found</option>';
      const newRadio = document.querySelector('input[name="ah-presentation-mode"][value="new"]');
      if (newRadio) {
        newRadio.checked = true;
        newRadio.dispatchEvent(new Event('change'));
      }
      return;
    }

    cachedPresentations.forEach(function (p) {
      const opt = document.createElement('option');
      opt.value = p.id;
      opt.textContent = p.title;
      selectPresentation.appendChild(opt);
    });
  }

  function getResolvedRugDimensions() {
    if (!currentProduct) return { width: null, length: null };

    if (currentProduct.isCustom) {
      const customWidth = combineRugDimensionInputs(dimWidthFt?.value, dimWidthIn?.value);
      const customLength = combineRugDimensionInputs(dimLengthFt?.value, dimLengthIn?.value);
      return { width: customWidth, length: customLength };
    }

    // Standard in-stock rug: parse dimensions from variant title or size
    const parsed = parseRugDimensionsFromText(currentProduct.size);
    if (parsed) {
      return { width: parsed.width, length: parsed.length };
    }

    return { width: null, length: null };
  }

  // -------------------------------------------------------------------------
  // Submissions
  // -------------------------------------------------------------------------

  async function handleSubmit() {
    if (isSubmitting || !currentProduct) return;

    setLoading(true);

    try {
      if (activeTab === 'project') {
        await handleProjectSubmit();
      } else {
        await handlePresentationSubmit();
      }
      closeModal();
    } catch (err) {
      console.error('Action failed:', err);
      showToast(err.message || 'Failed to save item. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleProjectSubmit() {
    const isNew = document.querySelector('input[name="ah-project-mode"]:checked')?.value === 'new';
    let targetProjectId = selectProject ? selectProject.value : '';
    let targetProjectName = '';

    if (isNew) {
      const newName = (newProjectNameInput?.value || '').trim();
      const newNotes = (newProjectNotesInput?.value || '').trim();

      if (!newName) {
        throw new Error('Please enter a project name.');
      }

      // 1. Create the new project
      const createdRes = await apiRequest('/api/client/projects', {
        method: 'POST',
        body: JSON.stringify({ name: newName, notes: newNotes }),
      });

      targetProjectId = createdRes?.project?.id || createdRes?.id;
      targetProjectName = newName;
    } else {
      if (!targetProjectId) {
        throw new Error('Please select an existing project or choose Create New.');
      }
      targetProjectName = selectProject.options[selectProject.selectedIndex]?.text || 'Project';
    }

    // 2. Add product to the project
    const room = (projectItemRoomInput?.value || '').trim();
    const notes = (projectItemNotesInput?.value || '').trim();
    const dims = getResolvedRugDimensions();

    await apiRequest('/api/client/projects/' + encodeURIComponent(targetProjectId) + '/items', {
      method: 'POST',
      body: JSON.stringify({
        shopify_product_id: currentProduct.productId,
        shopify_variant_id: currentProduct.variantId,
        title: currentProduct.title,
        sku: currentProduct.sku,
        image_url: currentProduct.image,
        price: currentProduct.price,
        vendor: currentProduct.vendor,
        product_type: currentProduct.productType,
        rug_width: dims.width,
        rug_length: dims.length,
        room: room || null,
        notes: notes || null,
      }),
    });

    const portalUrl = getFullPortalUrl('/client/projects/' + encodeURIComponent(targetProjectId));
    showToast(
      'Added <strong>' + escapeHtml(currentProduct.title) + '</strong> to project <em>' + escapeHtml(targetProjectName) + '</em>.',
      'success',
      portalUrl,
      'View Project →'
    );
  }

  function getFullPortalUrl(path) {
    let hubBase = (window.__AUTHENTICITY_HUB_URL__ || window.__AUTHENTICITY_API_URL__ || 'https://hub.authenticity.shop').trim().replace(/\/$/, '');
    if (hubBase.endsWith('/api')) {
      hubBase = hubBase.slice(0, -4);
    }
    return hubBase + (path.startsWith('/') ? path : '/' + path);
  }

  async function handlePresentationSubmit() {
    const isNew = document.querySelector('input[name="ah-presentation-mode"]:checked')?.value === 'new';
    let targetPresentationId = selectPresentation ? selectPresentation.value : '';
    let targetPresentationTitle = '';

    if (isNew) {
      const newTitle = (newPresentationTitleInput?.value || '').trim();
      const linkedProjectId = newPresentationProjectSelect?.value || undefined;

      if (!newTitle) {
        throw new Error('Please enter a presentation title.');
      }

      // 1. Create the new presentation
      const createdRes = await apiRequest('/api/client/presentations', {
        method: 'POST',
        body: JSON.stringify({
          title: newTitle,
          projectId: linkedProjectId || undefined,
        }),
      });

      targetPresentationId =
        createdRes?.presentationId ||
        createdRes?.presentation?.id ||
        createdRes?.id;

      if (!targetPresentationId) {
        throw new Error('Failed to create presentation. Please try again.');
      }

      targetPresentationTitle = newTitle;
    } else {
      if (!targetPresentationId) {
        throw new Error('Please select an existing presentation or choose Create New.');
      }
      targetPresentationTitle = selectPresentation.options[selectPresentation.selectedIndex]?.text || 'Presentation';
    }

    // 2. Add product slide to presentation with resolved dimensions
    const dims = getResolvedRugDimensions();

    await apiRequest('/api/client/presentations/add-products', {
      method: 'POST',
      body: JSON.stringify({
        presentationId: targetPresentationId,
        products: [
          {
            shopify_product_id: currentProduct.productId,
            shopify_variant_id: currentProduct.variantId,
            title: currentProduct.title,
            sku: currentProduct.sku,
            image_url: currentProduct.image,
            price: currentProduct.price,
            vendor: currentProduct.vendor,
            product_type: currentProduct.productType,
            rug_width: dims.width,
            rug_length: dims.length,
            material: currentProduct.material,
            materials: currentProduct.material,
            origin: currentProduct.origin,
            texture: currentProduct.texture,
            pile_height: currentProduct.pileHeight,
            pileHeight: currentProduct.pileHeight,
            construction: currentProduct.construction,
            description: currentProduct.description,
            tags: currentProduct.tags,
            variants: currentProduct.variants,
          },
        ],
      }),
    });

    const portalUrl = getFullPortalUrl('/client/presentations/' + encodeURIComponent(targetPresentationId) + '/editor');
    showToast(
      'Added <strong>' + escapeHtml(currentProduct.title) + '</strong> to presentation <em>' + escapeHtml(targetPresentationTitle) + '</em>.',
      'success',
      portalUrl,
      'Open Builder →'
    );
  }

  function setLoading(loading) {
    isSubmitting = loading;
    if (submitBtn) submitBtn.disabled = loading;
    if (submitSpinner) submitSpinner.style.display = loading ? 'inline-block' : 'none';
    if (submitText) submitText.style.display = loading ? 'none' : 'inline';
  }

  function showToast(message, type = 'success', actionUrl = null, actionText = null) {
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.id = 'ah-toast-container';
      toastContainer.className = 'ah-toast-container';
      document.body.appendChild(toastContainer);
    }

    const toast = document.createElement('div');
    toast.className = 'ah-toast ah-toast--' + type;

    let contentHtml = '<span>' + message + '</span>';
    if (actionUrl && actionText) {
      contentHtml += '<a href="' + actionUrl + '" target="_blank" rel="noopener noreferrer" class="ah-toast__link">' + actionText + '</a>';
    }

    toast.innerHTML = contentHtml;
    toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(10px)';
      toast.style.transition = 'all 0.25s ease';
      setTimeout(() => toast.remove(), 250);
    }, 4500);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

